import { useState } from "react";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { post } from "@common/api/apiShared";
import { Result } from "@common/api/result";
import { useDotLegalMutation, useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import { RiskAreaViewModel } from "../../../riskSettings/riskArea/riskAreas/RiskAreas.types";
import { SelectableRiskScenario } from "../../riskAssessmentsOverview/addRiskScenarioDialog/AddRiskScenarioDialog.types";
import { toLevelOptions, useMitigationPlanForm } from "../../riskAssessmentsOverview/treatmentDialogs/useMitigationPlanForm";
import { entityKey, entityTypeLabel } from "../RiskOperationalOverview.types";
import { IBulkMitigateDialog } from "./BulkMitigateDialog";
import { BulkMitigateEntityRow, OperationalBulkMitigationRequest, OperationalBulkMitigationResult } from "./BulkMitigateDialog.types";

export function useBulkMitigateDialog(props: IBulkMitigateDialog) {
    const { translateString } = useTranslation();
    const snackbar = useDotLegalSnackbar();

    // The overview selection is the single source of truth: the dialog renders props.entities live and removing
    // here calls back to the overview, which unchecks the row and re-renders with one fewer entity.
    const entities = props.entities;
    const [selectedScenarioId, setSelectedScenarioId] = useState<string | null>(null);
    const [scenarioError, setScenarioError] = useState("");

    const planForm = useMitigationPlanForm();

    // The scenario to mitigate is a single-select, decoupled from the list filter: options are the scenarios
    // applicable to the selected entities' types in this area.
    const distinctEntityTypes = Array.from(new Set(entities.map((entity) => entity.type)));
    const entityTypesQueryString = distinctEntityTypes.map((type) => `entityTypes=${type}`).join("&");
    const scenariosQuery = useDotLegalQuery<Array<SelectableRiskScenario>>(
        `/risk-assessments/${props.riskAreaId}/applicable-scenarios?${entityTypesQueryString}`,
        { enabled: distinctEntityTypes.length > 0 }
    );
    const scenarioOptions = (scenariosQuery.data ?? []).map((scenario) => ({ id: scenario.id, name: scenario.name }));

    // Every selected entity sits in the same risk area, so the expected-after-mitigation levels are the area's
    // consequence/probability levels — the same source the single-scenario dialog reads off its scenario row.
    const riskAreasQuery = useDotLegalQuery<Array<RiskAreaViewModel>>("/risk-areas");
    const riskArea = riskAreasQuery.data?.find((area) => area.id === props.riskAreaId);
    const consequenceOptions = toLevelOptions(riskArea?.consequenceLevels ?? []);
    const probabilityOptions = toLevelOptions(riskArea?.probabilityLevels ?? []);

    const entityRows: Array<BulkMitigateEntityRow> = entities.map((entity) => ({
        key: entityKey(entity.type, entity.entityId),
        type: entity.type,
        typeLabel: entityTypeLabel(entity.type, translateString),
        name: entity.name,
    }));

    const saveMutation = useDotLegalMutation(
        (request: OperationalBulkMitigationRequest) =>
            post<OperationalBulkMitigationResult>(`/risk-assessments/${props.riskAreaId}/bulk-mitigate-by-scenario`, request),
        {
            onSuccess: (response: Result<OperationalBulkMitigationResult>) => {
                if (response.success()) {
                    const { created, skipped } = response.value();
                    snackbar.show(
                        skipped > 0
                            ? translateString("bulkMitigationCreatedWithSkipped", { created, skipped })
                            : translateString("bulkMitigationCreated", { count: created })
                    );
                    props.onSaved();
                    props.onClose();
                }
            },
        }
    );

    const isValid = !planForm.validation.anyHasErrors && selectedScenarioId !== null && entities.length > 0;

    const onSave = () => {
        const hasScenario = selectedScenarioId !== null;
        setScenarioError(hasScenario ? "" : translateString("fieldRequired"));

        if (planForm.validation.anyHasErrors) {
            planForm.validation.setShowErrors(true);
            return;
        }

        if (!hasScenario || entities.length === 0) return;

        saveMutation.mutateAsync({
            riskScenarioId: selectedScenarioId!,
            entities: entities.map((entity) => ({ entityType: entity.type, entityId: entity.entityId })),
            ...planForm.toSavePayload(),
        });
    };

    return {
        planForm,
        entities,
        entityRows,
        removeEntity: props.onRemoveEntity,
        isLoading: scenariosQuery.isLoading || riskAreasQuery.isLoading,
        isValid,
        inProgress: saveMutation.isLoading,
        onSave,
        scenarioOptions,
        selectedScenarioId: selectedScenarioId ?? "",
        setSelectedScenarioId: (value: string | null) => {
            setSelectedScenarioId(value);
            setScenarioError(value ? "" : translateString("fieldRequired"));
        },
        scenarioError,
        consequenceOptions,
        probabilityOptions,
    };
}
